import React, { useEffect, useState } from 'react';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import ReviewForm from './ReviewForm';

import {
  Assessment,
  AssessmentDetail as AssessmentDetailType,
  AssessmentsAPI,
} from '@/services/assessmentsAPI';
import Stack from '@/components/Stack';
import Button from '@/components/Button';
import LoadingState from '@/components/LoadingState';
import Alert from '@/components/Alert';
import Chip from '@/components/Chip';
import { Title, Heading, Body, Small } from '@/components/Typography';

interface AssessmentDetailProps {
  assessment: Assessment;
  onReviewSuccess: () => void;
}

const AssessmentDetail: React.FC<AssessmentDetailProps> = ({
  assessment,
  onReviewSuccess,
}) => {
  const [detailedAssessment, setDetailedAssessment] =
    useState<AssessmentDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReviewForm, setShowReviewForm] = useState(false);

  useEffect(() => {
    loadAssessmentDetails();
  }, [assessment.external_id]);

  const loadAssessmentDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AssessmentsAPI.getAssessment(
        assessment.external_id,
      );
      setDetailedAssessment(response.data);
    } catch (err) {
      setError('Failed to load assessment details');
      console.error('Error loading assessment details:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getResultStatusColor = (
    resultStatus: string | null,
  ): 'success' | 'error' | 'warning' | 'primary' => {
    switch (resultStatus) {
      case 'passed':
        return 'success';
      case 'failed':
        return 'error';
      case 'for_review':
        return 'warning';
      default:
        return 'primary';
    }
  };

  if (loading) {
    return <LoadingState text="Loading assessment details..." />;
  }

  if (error || !detailedAssessment) {
    return (
      <Stack vertical gap={16} align="center">
        <Alert
          status="danger"
          icon={faExclamationTriangle}
          description={error || 'Failed to load assessment'}
        />
        <Button
          onClick={loadAssessmentDetails}
          a11yLabel="Retry loading assessment details"
          label="Retry"
        />
      </Stack>
    );
  }

  return (
    <Stack vertical gap={16}>
      <Stack justify="apart" align="center">
        <Title>Assessment Details</Title>
        {!detailedAssessment.reviewed_at && (
          <Button
            onClick={() => setShowReviewForm(!showReviewForm)}
            a11yLabel={showReviewForm ? 'Cancel review' : 'Review assessment'}
            label={showReviewForm ? 'Cancel Review' : 'Review Assessment'}
          />
        )}
      </Stack>

      <hr
        style={{
          border: 'none',
          borderTop: '1px solid #e0e0e0',
          margin: '8px 0',
        }}
      />

      {showReviewForm ? (
        <ReviewForm
          assessment={detailedAssessment}
          onSuccess={onReviewSuccess}
          onCancel={() => setShowReviewForm(false)}
        />
      ) : (
        <>
          <Stack vertical gap={16}>
            <Heading>Basic Information</Heading>
            <Stack vertical gap={8}>
              <Stack justify="apart">
                <Small color="light" weight="semibold">
                  User
                </Small>
                <Small>{detailedAssessment.user_name}</Small>
              </Stack>
              <Stack justify="apart">
                <Small color="light" weight="semibold">
                  Skill
                </Small>
                <Small>{detailedAssessment.skill_name}</Small>
              </Stack>
              <Stack justify="apart">
                <Small color="light" weight="semibold">
                  AI Status
                </Small>
                {detailedAssessment.result_status ? (
                  <Chip
                    color={getResultStatusColor(
                      detailedAssessment.result_status,
                    )}
                    label={detailedAssessment.result_status.replace(/_/g, ' ')}
                  />
                ) : (
                  <Small>N/A</Small>
                )}
              </Stack>
              {detailedAssessment.total_score !== null &&
                detailedAssessment.total_score !== undefined && (
                  <Stack justify="apart" align="center">
                    <Small color="light" weight="semibold">
                      Average Score
                    </Small>
                    <div
                      style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color:
                          detailedAssessment.total_score >= 8
                            ? '#44A735'
                            : detailedAssessment.total_score >= 6
                              ? '#DEA83E'
                              : '#DC1515',
                      }}
                    >
                      {detailedAssessment.total_score}
                    </div>
                  </Stack>
                )}
              <Stack justify="apart">
                <Small color="light" weight="semibold">
                  Created At
                </Small>
                <Small>{formatDate(detailedAssessment.created_at)}</Small>
              </Stack>
            </Stack>
          </Stack>

          {(detailedAssessment.overall_feedback ||
            detailedAssessment.overall_recommendation) && (
            <Stack vertical gap={16}>
              <Heading>Overall Feedback</Heading>
              <Stack vertical gap={8}>
                {detailedAssessment.overall_feedback && (
                  <Stack vertical gap={4}>
                    <Small color="light" weight="semibold">
                      Feedback
                    </Small>
                    <Body>{detailedAssessment.overall_feedback}</Body>
                  </Stack>
                )}
                {detailedAssessment.overall_recommendation && (
                  <Stack vertical gap={4}>
                    <Small color="light" weight="semibold">
                      Recommendation
                    </Small>
                    <Body>{detailedAssessment.overall_recommendation}</Body>
                  </Stack>
                )}
              </Stack>
            </Stack>
          )}

          {detailedAssessment.reviewed_at && (
            <Stack vertical gap={16}>
              <Heading>Review Information</Heading>
              <Stack vertical gap={8}>
                <Stack justify="apart">
                  <Small color="light" weight="semibold">
                    Final Status
                  </Small>
                  {detailedAssessment.final_status ? (
                    <Chip
                      color={getResultStatusColor(
                        detailedAssessment.final_status,
                      )}
                      label={detailedAssessment.final_status.replace(/_/g, ' ')}
                    />
                  ) : (
                    <Small>N/A</Small>
                  )}
                </Stack>
                <Stack justify="apart" align="center">
                  <Small color="light" weight="semibold">
                    Final Score
                  </Small>
                  {detailedAssessment.final_score !== null &&
                  detailedAssessment.final_score !== undefined ? (
                    <div
                      style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color:
                          detailedAssessment.final_score >= 8
                            ? '#44A735'
                            : detailedAssessment.final_score >= 6
                              ? '#DEA83E'
                              : '#DC1515',
                      }}
                    >
                      {detailedAssessment.final_score}
                    </div>
                  ) : (
                    <Small>N/A</Small>
                  )}
                </Stack>
                <Stack justify="apart">
                  <Small color="light" weight="semibold">
                    Reviewed By
                  </Small>
                  <Small>{detailedAssessment.reviewed_by_name || 'N/A'}</Small>
                </Stack>
                <Stack justify="apart">
                  <Small color="light" weight="semibold">
                    Reviewed At
                  </Small>
                  <Small>{formatDate(detailedAssessment.reviewed_at)}</Small>
                </Stack>
                {detailedAssessment.notes && (
                  <Stack vertical gap={4}>
                    <Small color="light" weight="semibold">
                      Notes
                    </Small>
                    <Body>{detailedAssessment.notes}</Body>
                  </Stack>
                )}
              </Stack>
            </Stack>
          )}

          {detailedAssessment.questions &&
            detailedAssessment.questions.length > 0 && (
              <Stack vertical gap={16}>
                <Heading>
                  Questions & Answers ({detailedAssessment.questions.length})
                </Heading>
                <Stack vertical gap={16}>
                  {detailedAssessment.questions.map((question, index) => (
                    <div
                      key={question.id}
                      style={{
                        backgroundColor: '#f9f9f9',
                        padding: '16px',
                        borderRadius: '8px',
                        width: '100%',
                      }}
                    >
                      <Stack vertical gap={16}>
                        <Stack justify="apart" align="center">
                          <Chip
                            color="primary"
                            label={`Question ${index + 1}`}
                          />
                          {question.answer && (
                            <div
                              style={{
                                fontSize: '24px',
                                fontWeight: 'bold',
                                color:
                                  question.answer.score >= 8
                                    ? '#44A735'
                                    : question.answer.score >= 6
                                      ? '#DEA83E'
                                      : '#DC1515',
                              }}
                            >
                              {question.answer.score}
                            </div>
                          )}
                        </Stack>

                        <Body>{question.question_text}</Body>

                        {question.answer ? (
                          <>
                            <Stack vertical gap={4}>
                              <Small weight="semibold">
                                Answer Transcript:
                              </Small>
                              <div
                                style={{
                                  backgroundColor: '#f0f7ff',
                                  padding: '12px 16px',
                                  borderRadius: '6px',
                                  borderLeft: '3px solid #4a90e2',
                                  color: '#2c3e50',
                                }}
                              >
                                <Body>{question.answer.transcript}</Body>
                              </div>
                            </Stack>
                            {question.answer.recording_url && (
                              <Stack vertical gap={4}>
                                <Small weight="semibold">
                                  Answer Recording:
                                </Small>
                                <audio controls style={{ width: '100%' }}>
                                  <source
                                    src={question.answer.recording_url}
                                    type="audio/mpeg"
                                  />
                                  Your browser does not support the audio
                                  element.
                                </audio>
                              </Stack>
                            )}
                            {question.answer.ai_feedback && (
                              <Stack vertical gap={4}>
                                <Small weight="semibold">AI Feedback:</Small>
                                <div
                                  style={{
                                    backgroundColor: '#FFF4E6',
                                    padding: '12px 16px',
                                    borderRadius: '6px',
                                    borderLeft: '3px solid #DEA83E',
                                    color: '#8B6914',
                                  }}
                                >
                                  <Body>
                                    {typeof question.answer.ai_feedback ===
                                    'string'
                                      ? question.answer.ai_feedback
                                      : JSON.stringify(
                                          question.answer.ai_feedback,
                                          null,
                                          2,
                                        )}
                                  </Body>
                                </div>
                              </Stack>
                            )}
                          </>
                        ) : (
                          <Body style={{ fontStyle: 'italic' }}>
                            No answer provided
                          </Body>
                        )}

                        {question.category && (
                          <Chip color="success" label={question.category} />
                        )}
                      </Stack>
                    </div>
                  ))}
                </Stack>
              </Stack>
            )}
        </>
      )}
    </Stack>
  );
};

export default AssessmentDetail;
